import { useState, useEffect } from 'react';
import { ListingTemplate, ListingTemplateVariation } from '../types';
import { supabase } from '../lib/supabase';

export const useListingTemplates = (userId: string | null) => {
  const [templates, setTemplates] = useState<ListingTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (userId) {
      loadTemplates();
    } else {
      setTemplates([]);
      setIsLoading(false);
    }
  }, [userId]);

  const loadTemplates = async () => {
    if (!userId) return;

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('listing_templates')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Error loading listing templates:', fetchError);
        setError(fetchError.message);
        return;
      }

      const templates = (data || []).map(item => ({
        id: item.id,
        userId: item.user_id,
        name: item.name,
        masterTitle: item.master_title,
        masterDescription: item.master_description,
        imageUrl: item.image_url,
        lockedElements: item.locked_elements || [],
        createdAt: new Date(item.created_at),
        updatedAt: new Date(item.updated_at),
      }));

      setTemplates(templates);
    } catch (err) {
      console.error('Error loading listing templates:', err);
      setError('Failed to load listing templates');
    } finally {
      setIsLoading(false);
    }
  };

  const createTemplate = async (templateData: {
    name: string;
    masterTitle: string;
    masterDescription: string;
    imageUrl?: string;
    lockedElements?: string[];
  }): Promise<ListingTemplate | null> => {
    if (!userId) return null;

    try {
      const { data, error } = await supabase
        .from('listing_templates')
        .insert({
          user_id: userId,
          name: templateData.name,
          master_title: templateData.masterTitle,
          master_description: templateData.masterDescription,
          image_url: templateData.imageUrl,
          locked_elements: templateData.lockedElements || [],
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating listing template:', error);
        throw new Error(error.message);
      }

      const newTemplate: ListingTemplate = {
        id: data.id,
        userId: data.user_id,
        name: data.name,
        masterTitle: data.master_title,
        masterDescription: data.master_description,
        imageUrl: data.image_url,
        lockedElements: data.locked_elements || [],
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
      };

      setTemplates(prev => [newTemplate, ...prev]);
      return newTemplate;
    } catch (err) {
      console.error('Error creating listing template:', err);
      throw err;
    }
  };

  const updateTemplate = async (
    id: string,
    updates: Partial<ListingTemplate>
  ): Promise<void> => {
    try {
      const updateData: any = {};
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.masterTitle !== undefined) updateData.master_title = updates.masterTitle;
      if (updates.masterDescription !== undefined) updateData.master_description = updates.masterDescription;
      if (updates.imageUrl !== undefined) updateData.image_url = updates.imageUrl;
      if (updates.lockedElements !== undefined) updateData.locked_elements = updates.lockedElements;

      const { error } = await supabase
        .from('listing_templates')
        .update(updateData)
        .eq('id', id);

      if (error) {
        console.error('Error updating listing template:', error);
        throw new Error(error.message);
      }

      setTemplates(prev =>
        prev.map(template =>
          template.id === id ? { ...template, ...updates } : template
        )
      );
    } catch (err) {
      console.error('Error updating listing template:', err);
      throw err;
    }
  };

  const deleteTemplate = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('listing_templates')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting listing template:', error);
        throw new Error(error.message);
      }

      setTemplates(prev => prev.filter(template => template.id !== id));
    } catch (err) {
      console.error('Error deleting listing template:', err);
      throw err;
    }
  };

  const getTemplateById = async (id: string): Promise<ListingTemplate | null> => {
    try {
      const { data, error } = await supabase
        .from('listing_templates')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error loading listing template:', error);
        throw new Error(error.message);
      }

      return {
        id: data.id,
        userId: data.user_id,
        name: data.name,
        masterTitle: data.master_title,
        masterDescription: data.master_description,
        imageUrl: data.image_url,
        lockedElements: data.locked_elements || [],
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
      };
    } catch (err) {
      console.error('Error loading listing template:', err);
      throw err;
    }
  };

  const addVariation = async (
    templateId: string,
    variationData: {
      variationInstruction: string;
      generatedTitle: string;
      generatedDescription: string;
    }
  ): Promise<ListingTemplateVariation> => {
    try {
      const { data, error } = await supabase
        .from('listing_template_variations')
        .insert({
          template_id: templateId,
          variation_instruction: variationData.variationInstruction,
          generated_title: variationData.generatedTitle,
          generated_description: variationData.generatedDescription,
        })
        .select()
        .single();

      if (error) {
        console.error('Error adding template variation:', error);
        throw new Error(error.message);
      }

      return {
        id: data.id,
        templateId: data.template_id,
        variationInstruction: data.variation_instruction,
        generatedTitle: data.generated_title,
        generatedDescription: data.generated_description,
        createdAt: new Date(data.created_at),
      };
    } catch (err) {
      console.error('Error adding template variation:', err);
      throw err;
    }
  };

  const getTemplateVariations = async (
    templateId: string
  ): Promise<ListingTemplateVariation[]> => {
    try {
      const { data, error } = await supabase
        .from('listing_template_variations')
        .select('*')
        .eq('template_id', templateId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading template variations:', error);
        throw new Error(error.message);
      }

      return (data || []).map(item => ({
        id: item.id,
        templateId: item.template_id,
        variationInstruction: item.variation_instruction,
        generatedTitle: item.generated_title,
        generatedDescription: item.generated_description,
        createdAt: new Date(item.created_at),
      }));
    } catch (err) {
      console.error('Error loading template variations:', err);
      throw err;
    }
  };

  const deleteVariation = async (variationId: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('listing_template_variations')
        .delete()
        .eq('id', variationId);

      if (error) {
        console.error('Error deleting template variation:', error);
        throw new Error(error.message);
      }
    } catch (err) {
      console.error('Error deleting template variation:', err);
      throw err;
    }
  };

  return {
    templates,
    isLoading,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplateById,
    addVariation,
    getTemplateVariations,
    deleteVariation,
    refreshTemplates: loadTemplates,
  };
};